<template>
  <div>
    <div class="login-container">
      <loading v-if="isLoading" />
      <div v-else>
        <div>
          <img class="logo" :src="logo" />
          <div v-show="version" class="version">Version {{version}}</div>
          <div v-show="showForgottenPassword" class="has-text-primary margin-top-2 is-size-5">Password Reset</div>
        </div>

        <form v-show="!showForgottenPassword" class="login-form" autocomplete="off" @submit.prevent="login">
          <div class="field">
            <label class="label is-sr-only">Username</label>
            <div class="control has-icons-left">
              <input class="input" :class="{'is-danger' : $v.loginData.$dirty && $v.loginData.username.$error}" type="text" placeholder="Username" v-model="$v.loginData.username.$model" />
              <span class="icon is-medium is-left">
                <fa-icon :icon="faUser" />
              </span>
            </div>
            <div class="help is-danger" v-if="$v.loginData.$dirty && !$v.loginData.username.required">Please enter your username.</div>
          </div>

          <div class="field">
            <label class="label is-sr-only">Password</label>
            <div class="control has-icons-left">
              <input class="input" :class="{'is-danger' : $v.loginData.$dirty && $v.loginData.password.$error}" type="password" placeholder="Password" v-model="$v.loginData.password.$model" />
              <span class="icon is-medium is-left">
                <fa-icon :icon="faLock" />
              </span>
            </div>
            <div class="help is-danger" v-if="$v.loginData.$dirty && !$v.loginData.password.required">Please enter your password.</div>
          </div>

          <div class="field">
            <button type="submit" class="button is-primary is-pulled-right">Login</button>
            <a href="#" class="has-text-primary is-size-7" @click="showForgottenPassword = true">Forgotten your password?</a>
            <div class="copyright">{{copyrightText}}</div>
          </div>
        </form>

        <form v-show="showForgottenPassword" class="login-form" autocomplete="off" @submit.prevent="resetPassword">
          <div class="field">
            <label class="label is-sr-only">Username</label>
            <div class="control has-icons-left">
              <input class="input" :class="{'is-danger' : $v.resetPasswordData.$dirty && $v.resetPasswordData.username.$error}" type="text" placeholder="Username" v-model="$v.resetPasswordData.username.$model" />
              <span class="icon is-medium is-left">
                <fa-icon :icon="faUser" />
              </span>
            </div>
            <div class="help is-danger" v-if="$v.resetPasswordData.$dirty && !$v.resetPasswordData.username.required">Please enter your username.</div>
          </div>

          <div class="field">
            <button type="submit" class="button is-primary is-pulled-right">Reset</button>
            <a href="#" class="has-text-primary is-size-7" @click="showForgottenPassword = false">Cancel</a>
            <div class="copyright">{{copyrightText}}</div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import { LOGIN, LOAD_SESSION } from "@/stores/application/actions"
import { VERSION, HAS_SESSION } from "@/stores/application/getters"
import { PREVIOUS_CREDENTIALS_KEY, ROUTE_AFTER_LOGIN_KEY } from "@/constants"
import logo from "@/assets/logo-grey.png"
import { required } from "vuelidate/lib/validators"
import loading from "@/components/loading"
import { faUser, faLock } from "@fortawesome/free-solid-svg-icons"

export default {
  components: {
    loading
  },
  data() {
    return {
      logo,
      isLoading: true,
      loginData: {
        username: "",
        password: ""
      },
      resetPasswordData: {
        username: ""
      },
      showForgottenPassword: false,
      copyrightText: "© Warners Group Publications Plc 2020",
      faUser,
      faLock
    }
  },
  computed: {
    ...mapGetters({
      version: VERSION,
      hasSession: HAS_SESSION
    })
  },
  methods: {
    login() {
      this.$v.loginData.$touch()

      if (this.$v.loginData.$invalid)
        return

      this.isLoading = true

      this.$store
        .dispatch(LOGIN, this.loginData)
        .then(() => {
          this.storeCredentials()

          this.$store.dispatch(LOAD_SESSION).then(() => {
            this.goToNextRoute();
          });
        })
        .catch(error => {
          if (error.response.data && error.response.data.length > 0) {
            this.$alert(error.response.data, "", "error");
          } else this.$alert("An unexpected error occurred. Please try again.", "", "error");

          this.isLoading = false
        });
    },
    resetPassword() {
      this.$v.resetPasswordData.$touch()

      if (this.$v.resetPasswordData.$invalid)
        return

      this.isLoading = true

      this.$http.post("Authentication/forgotten-password", this.resetPasswordData).then(() => {
        this.$alert("A link to reset your password will be sent to your email address if it matches a valid account.", "", "success");
        this.loginData.password = ""
        this.showForgottenPassword = false
        this.isLoading = false
      }).catch((error) => {
        if (error.response.data && error.response.data.length > 0) {
          this.$alert(error.response.data, "", "error");
        } else this.$alert("An unexpected error occurred. Please try again.", "", "error");

        this.isLoading = false
      });
    },
    storeCredentials() {
      var credentials = Object.assign({}, this.loginData)
      delete credentials.password;
      localStorage.setItem(PREVIOUS_CREDENTIALS_KEY, JSON.stringify(credentials))
    },
    goToNextRoute() {
      var routeAfterLogin = sessionStorage.getItem(ROUTE_AFTER_LOGIN_KEY);

      if (routeAfterLogin) {
        sessionStorage.removeItem(ROUTE_AFTER_LOGIN_KEY)
        this.$router.push(routeAfterLogin)
      } else this.$router.push("/")
    }
  },
  mounted() {
    this.$store.dispatch(LOAD_SESSION).then(() => {
      this.goToNextRoute();
      return;
    }).catch((error) => {
      var previousCredentials = localStorage.getItem(PREVIOUS_CREDENTIALS_KEY)

      if (previousCredentials)
        this.loginData = JSON.parse(previousCredentials)

      this.isLoading = false
    });
  },
  validations: {
    loginData: {
      username: { required },
      password: { required }
    },
    resetPasswordData: {
      username: { required }
    }
  }
}
</script>

<style lang="sass" scoped>
.login-container
  text-align: center
  align-items: center
  position: absolute
  width: 320px
  height: 450px
  top: 50%
  left: 50%
  margin-top: -225px
  margin-left: -160px
.login-form
  padding: 15px
  text-align: left
.logo
  width: 150px
.version
  margin-top: -10px
  font-size: 14px
.copyright
  text-align: center
  font-size: 12px
  display: inline-block
.button
  margin-top: 7px
</style>